import React, { useState } from 'react';
import { CheckCircle, ArrowLeft, ArrowRight, FileText, Edit3, Check, X, ChevronDown, ChevronUp, Save } from 'lucide-react';
import api from '../../services/api';

const ImportConfirmation = ({ importData, onContinue, onBack, onBackToDashboard }) => {
  const [assessmentName, setAssessmentName] = useState(importData?.name || 'Untitled Assessment');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [showItemsList, setShowItemsList] = useState(false);
  
  // Use items passed through from import response
  const importedItems = importData?.imported_items || [];
  
  const handleSaveName = async (newName) => {
    const trimmedName = newName.trim();
    
    if (!trimmedName) {
      setNameError('Assessment name cannot be empty');
      setAssessmentName(importData.name || 'Untitled Assessment');
      return;
    }
    
    // Don't save if name hasn't changed
    if (trimmedName === importData.name) {
      return;
    }
    
    setIsSavingName(true);
    setNameError('');
    
    try {
      await api.patch(`/api/assessment-builder/assessments/${importData.assessment_uuid}/`, { name: trimmedName });
      // Update the importData name
      importData.name = trimmedName;
    } catch (error) {
      console.error('Error updating assessment name:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update name';
      setNameError(errorMsg);
      // Revert to original name on error
      setAssessmentName(importData.name || 'Untitled Assessment');
    } finally {
      setIsSavingName(false);
    }
  };
  // Use data from import response directly - no need to fetch again
  if (!importData?.assessment_id) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-900 mb-2">No Assessment Data</h2>
            <p className="text-red-700 mb-4">Import data is missing</p>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getLibraryTypeName = (type) => {
    const types = {
      'vpg': 'VPG Global Library',
      'sponsor': 'Sponsor Library',
      'study': 'Study-Specific Library'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Success Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Assessment Imported Successfully!
              </h1>
              <p className="text-gray-600">
                {importData.message || `Successfully imported "${importData.name}" with ${importData.items_imported} items`}
              </p>
              {importData.items_reused > 0 && (
                <p className="text-sm text-blue-600 mt-2">
                  ℹ️ {importData.items_created} new questions added to Item Bank, {importData.items_reused} reused from existing library
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Assessment Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Assessment Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={assessmentName}
                  onChange={(e) => {
                    setAssessmentName(e.target.value);
                    setNameError('');
                  }}
                  onBlur={(e) => handleSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  disabled={isSavingName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Enter assessment name"
                />
                {isSavingName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Save className="w-4 h-4 text-primary-600 animate-pulse" />
                  </div>
                )}
              </div>
              {nameError && (
                <p className="mt-1 text-sm text-red-600">{nameError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Library Location</label>
              <p className="text-lg font-medium text-gray-900">{getLibraryTypeName(importData.library_type)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Items Imported</label>
              <button
                onClick={() => setShowItemsList(!showItemsList)}
                className="text-lg font-medium text-primary-600 hover:text-primary-700 flex items-center gap-2 transition-colors"
              >
                <span>{importData.items_imported || 0} items</span>
                {showItemsList ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assessment ID</label>
              <p className="text-lg font-medium text-gray-900">#{importData.assessment_id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Survey ID</label>
              <p className="text-lg font-medium text-gray-900">#{importData.survey_id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <p className="text-lg font-medium text-gray-900">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm">Draft</span>
              </p>
            </div>
          </div>
        </div>

        {/* Imported Items List */}
        {showItemsList && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Imported Items ({importData.items_imported})
              </h3>
              {importedItems.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {importedItems.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {item.item_text || item.text || item.question_text || 'Untitled Question'}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            {item.question_type && (
                              <span className="px-2 py-1 bg-gray-100 rounded">
                                {item.question_type}
                              </span>
                            )}
                            {item.response_type && (
                              <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">
                                {item.response_type}
                              </span>
                            )}
                            {item.category && (
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                                {item.category}
                              </span>
                            )}
                            {item.domain && (
                              <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded">
                                {item.domain}
                              </span>
                            )}
                            {item.source && (
                              <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                                {item.source}
                              </span>
                            )}
                            {item.validated && (
                              <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded font-medium">
                                ✓ Validated
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No items to display</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Validation Warnings */}
        {importData.validation_warnings && importData.validation_warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-900 mb-3">Import Warnings</h3>
            <ul className="space-y-2">
              {importData.validation_warnings.map((warning, index) => (
                <li key={index} className="text-sm text-yellow-800 flex items-start">
                  <span className="mr-2">⚠</span>
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Steps Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">What's Next?</h3>
              <p className="text-blue-800 mb-3">
                Your assessment has been imported successfully with {importData.items_imported} items. Next steps:
              </p>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Continue to Screen Builder to design the assessment layout</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Configure item properties and response validation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Add conditional logic and skip patterns if needed</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Preview and test before publishing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onBackToDashboard || onBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
          
          <button
            onClick={() => onContinue(importData)}
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Continue to Screen Builder
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportConfirmation;
